import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import StatusBadge from '@/components/ui/StatusBadge';
import { ApiError } from '@/lib/api';
import { onboardingApi, type PartnerFull } from '@/lib/onboarding';
import { daysBetween, num, shortDate } from '@/lib/format';

const statuses = ['All', 'Onboarding', 'Active', 'Paused', 'Negotiating'] as const;
type Filter = (typeof statuses)[number];
const STALE_DAYS = 7;

/** Список поставщиков на реальных данных (/api/partners): статус, контакт, артикулы, последний прайс, переход в карточку. */
export default function PartnersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('All');
  const [query, setQuery] = useState('');
  const [partners, setPartners] = useState<PartnerFull[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onboardingApi.partners().then(setPartners).catch((e: ApiError) => setError(e.code));
  }, []);

  const all = useMemo(() => partners ?? [], [partners]);
  const today = new Date().toISOString().slice(0, 10);
  const staleOf = (p: PartnerFull) => (p.lastPriceListAt ? daysBetween(p.lastPriceListAt.slice(0, 10), today) : null);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((p) => (filter === 'All' || p.status === filter) && (!q || [p.name, p.shortName, p.contact, p.city, p.email].some((v) => (v ?? '').toLowerCase().includes(q))));
  }, [all, filter, query]);

  const totalSkus = all.reduce((s, p) => s + (p.skus ?? 0), 0);
  const withDiscount = all.filter((p) => p.discount > 0);
  const avgDiscount = withDiscount.length ? withDiscount.reduce((s, p) => s + p.discount, 0) / withDiscount.length : 0;
  const staleCount = all.filter((p) => p.status === 'Active' && (staleOf(p) == null || (staleOf(p) as number) > STALE_DAYS)).length;
  const toneOf = (status: string) => (status === 'Active' ? 'primary' : status === 'Paused' ? 'danger' : 'accent');

  const stats = [
    { label: t('partners.statNetwork'), value: String(all.length), hint: t('pc.onboardingCount', { count: all.filter((p) => p.status === 'Onboarding').length }), icon: 'ri-store-2-line' },
    { label: t('partners.statSkus'), value: num(totalSkus), hint: '', icon: 'ri-price-tag-3-line' },
    { label: t('partners.statDiscount'), value: withDiscount.length ? `${avgDiscount.toFixed(1)}%` : '—', hint: '', icon: 'ri-percent-line' },
    { label: t('pc.statStale'), value: String(staleCount), hint: t('pc.staleHint', { days: STALE_DAYS }), icon: 'ri-time-line' },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t('partners.eyebrow')}
        title={t('partners.title')}
        description={t('partners.description')}
        actions={
          <>
            <Link to="/partners/new" className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600">
              <i className="ri-store-3-line text-[14px]" /> {t('onb.newPartner')}
            </Link>
            <Link to="/import" className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 transition-colors hover:bg-background-100">
              <i className="ri-upload-cloud-2-line text-[14px]" /> {t('partners.importList')}
            </Link>
          </>
        }
      />

      {error && <p className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">{t('onb.error', { code: error })}</p>}

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <p className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-foreground-500">{s.label}</p>
              <i className={`${s.icon} text-[14px] text-foreground-300`} />
            </div>
            <p className="tnum mt-1.5 text-lg font-semibold text-foreground-950">{s.value}</p>
            {s.hint && <p className="text-[10.5px] text-foreground-400">{s.hint}</p>}
          </div>
        ))}
      </div>

      <Panel padded={false}>
        <div className="flex flex-col gap-3 border-b border-background-200 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-1 rounded-md border border-background-200 bg-background-100 p-1">
            {statuses.map((s) => (
              <button key={s} type="button" onClick={() => setFilter(s)} className={`cursor-pointer whitespace-nowrap rounded px-2.5 py-1 text-[11.5px] font-medium transition-colors ${filter === s ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'}`}>
                {s === 'All' ? t('common.all') : t(`status.${s}`)}
                <span className="tnum ml-1 text-foreground-400">{s === 'All' ? all.length : all.filter((p) => p.status === s).length}</span>
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-[280px]">
            <i className="ri-search-line pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-foreground-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('partners.searchPlaceholder')} aria-label={t('partners.searchPlaceholder')} className="h-8 w-full rounded-md border border-background-200 bg-background-50 pl-8 pr-2 text-[12px] outline-none focus:border-primary-400" />
          </div>
        </div>

        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[960px] border-collapse text-[12.5px]">
            <thead>
              <tr className="sticky top-0 border-b border-background-200 bg-background-100/80 text-left text-[10.5px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="px-4 py-2.5 font-medium">{t('partners.colPartner')}</th>
                <th className="px-3 py-2.5 font-medium">{t('partners.colContact')}</th>
                <th className="px-3 py-2.5 font-medium">{t('partners.colPhone')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('partners.colSkus')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('partners.colDiscount')}</th>
                <th className="px-3 py-2.5 font-medium">{t('partners.colLast')}</th>
                <th className="px-3 py-2.5 font-medium">{t('pc.colContract')}</th>
                <th className="px-4 py-2.5 font-medium">{t('partners.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const stale = staleOf(p);
                const phone = p.whatsapp || p.phone;
                return (
                  <tr key={p.id} onClick={() => navigate(`/partners/${p.id}`)} className="group cursor-pointer border-b border-background-200/70 transition-colors last:border-0 hover:bg-primary-50/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-secondary-100 text-[10px] font-semibold text-secondary-800">{(p.shortName || p.name).slice(0, 2).toUpperCase()}</span>
                        <div className="min-w-0">
                          <p className="max-w-[260px] truncate font-medium text-foreground-900 group-hover:text-primary-700">{p.name}</p>
                          <p className="truncate text-[11px] text-foreground-400">{[p.warehouse, p.city].filter(Boolean).join(' · ') || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-foreground-800">{p.contact || '—'}</p>
                      <p className="text-[11px] text-foreground-400">{p.role || p.email || ''}</p>
                    </td>
                    <td className="px-3 py-3">
                      {phone ? (
                        <div className="flex items-center gap-1.5">
                          <a href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" aria-label={`WhatsApp ${p.contact ?? p.name}`} onClick={(e) => e.stopPropagation()} className="flex h-6 w-6 items-center justify-center rounded bg-primary-50 text-primary-700 transition-colors hover:bg-primary-100">
                            <i className="ri-whatsapp-line text-[13px]" />
                          </a>
                          <span className="tnum text-[11.5px] text-foreground-600">{phone}</span>
                        </div>
                      ) : (
                        <span className="text-foreground-400">—</span>
                      )}
                    </td>
                    <td className="tnum px-3 py-3 text-right text-foreground-700">{p.skus ?? 0}</td>
                    <td className="tnum px-3 py-3 text-right font-medium text-foreground-900">{p.discount > 0 ? `${p.discount}%` : '—'}</td>
                    <td className="px-3 py-3">
                      {p.lastPriceListAt ? (
                        <div className="flex items-center gap-1.5">
                          <span className="tnum text-foreground-700">{shortDate(p.lastPriceListAt)}</span>
                          <span className="tnum text-[11px] text-foreground-400">v{p.lastPriceListVersion}</span>
                          {stale != null && stale > STALE_DAYS && <StatusBadge tone="accent" className="!px-1.5 !text-[10px]">{t('partners.stale')}</StatusBadge>}
                        </div>
                      ) : (
                        <span className="text-[11.5px] text-foreground-400">{t('ov.noList')}</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge tone={p.contractStatus === 'Signed' ? 'primary' : p.contractStatus === 'Expired' ? 'danger' : 'neutral'} dot={false}>{t(`status.${p.contractStatus}`, p.contractStatus)}</StatusBadge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <StatusBadge tone={toneOf(p.status) as 'primary' | 'danger' | 'accent'}>{t(`status.${p.status}`)}</StatusBadge>
                        <Link to={`/partners/${p.id}/onboarding`} onClick={(e) => e.stopPropagation()} title={t('onb.openOnboarding')} aria-label={t('onb.openOnboarding')} className="flex h-6 w-6 items-center justify-center rounded border border-background-200 text-foreground-400 transition-colors hover:border-primary-300 hover:text-primary-700">
                          <i className="ri-route-line text-[12px]" />
                        </Link>
                        <i className="ri-arrow-right-s-line text-[15px] text-foreground-300 group-hover:text-primary-500" />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {partners && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-[12px] text-foreground-500">{t('partners.empty')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-1 border-t border-background-200 px-4 py-2.5 text-[11px] text-foreground-400 sm:flex-row sm:items-center sm:justify-between">
          <p>{t('pc.footer', { shown: rows.length, total: all.length })}</p>
          <p>{t('pc.footerNote')}</p>
        </div>
      </Panel>
    </div>
  );
}
